import { apiRequest } from '../lib/api-client';

/** Ad accounts and the campaign report. */

export const AD_NETWORKS = ['LOCAL', 'META', 'GOOGLE'] as const;
export type AdNetwork = (typeof AD_NETWORKS)[number];

export interface AdAccount {
  id: string;
  network: AdNetwork;
  name: string;
  externalId: string | null;
  currency: string;
  status: 'CONNECTED' | 'EXPIRED' | 'REVOKED' | 'DISABLED';
  statusDetail: string | null;
  lastSyncedAt: string | null;
  nextSyncAt: string;
  syncError: string | null;
  createdAt: string;
}

export interface CampaignReportRow {
  id: string;
  accountId: string;
  accountName: string;
  network: AdNetwork;
  externalId: string;
  name: string;
  status: string;
  objective: string | null;
  dailyBudgetCents: number | null;
  utmCampaign: string | null;
  spendCents: number;
  impressions: number | null;
  clicks: number | null;
  networkLeads: number | null;
  conversions: number | null;
  crmLeads: number;
  won: number;
  revenueCents: number;
  daysReported: number;
  costPerLeadCents: number | null;
  costPerWonCents: number | null;
  returnOnSpend: number | null;
}

export interface Report {
  from: string;
  to: string;
  totals: {
    spendCents: number;
    impressions: number | null;
    clicks: number | null;
    networkLeads: number | null;
    crmLeads: number;
    won: number;
    revenueCents: number;
    costPerLeadCents: number | null;
    costPerWonCents: number | null;
    returnOnSpend: number | null;
  };
  campaigns: CampaignReportRow[];
  series: { day: string; spendCents: number; clicks: number | null; networkLeads: number | null }[];
}

export function listAdAccounts(): Promise<{ accounts: AdAccount[] }> {
  return apiRequest('/v1/ads/accounts');
}

export function connectAdAccount(input: {
  network: AdNetwork;
  name: string;
  externalId?: string | null;
  config?: Record<string, unknown>;
  currency?: string;
}): Promise<AdAccount> {
  return apiRequest('/v1/ads/accounts', { method: 'POST', body: input });
}

export function disconnectAdAccount(id: string): Promise<void> {
  return apiRequest(`/v1/ads/accounts/${id}`, { method: 'DELETE' });
}

export function syncAdAccount(id: string): Promise<{ queued: boolean }> {
  return apiRequest(`/v1/ads/accounts/${id}/sync`, { method: 'POST' });
}

export function adsReport(range: {
  from: string;
  to: string;
  accountId?: string;
}): Promise<Report> {
  const params = new URLSearchParams({ from: range.from, to: range.to });
  if (range.accountId) params.set('accountId', range.accountId);

  return apiRequest(`/v1/ads/report?${params.toString()}`);
}

export function declareCampaignUtm(
  id: string,
  utmCampaign: string | null,
): Promise<CampaignReportRow> {
  return apiRequest(`/v1/ads/campaigns/${id}`, { method: 'PATCH', body: { utmCampaign } });
}
